const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/
const YOUTUBE_HOST_PATTERN = /(^|\.)youtube\.com$/i
const YOUTUBE_NOCOOKIE_HOST_PATTERN = /(^|\.)youtube-nocookie\.com$/i
const YOUTU_BE_HOST_PATTERN = /(^|\.)youtu\.be$/i

const PLAYER_PHASES = {
  POSTER: "poster",
  AUTOPLAY_ATTEMPTING: "autoplay-attempting",
  PLAYING: "playing",
  MANUAL_PLAY_REQUIRED: "manual-play-required",
  PAUSED: "paused",
  PROVIDER_ERROR: "provider-error",
}

const ATTEMPT_MODES = {
  AUTOPLAY: "autoplay",
  MANUAL: "manual",
}

const isDataSaverEnabled = (connection) =>
  Boolean(connection && connection.saveData === true)

const isAutoplayViewportEligible = (entry) =>
  Boolean(
    entry &&
      entry.isIntersecting === true &&
      Number.isFinite(entry.intersectionRatio) &&
      entry.intersectionRatio >= 0.51,
  )

const shouldAutoplay = ({ reducedMotion = false, connection } = {}) =>
  !reducedMotion && !isDataSaverEnabled(connection)

// 自動再生しない時も、プレーヤーを止めたまま置く（Google が動画を見つけられるように、仕様 0023）。
// データ節約の人には通信を増やさないようサムネイルだけ。配信元の iframe ができる前のエラーの後もサムネイルに戻す
const shouldShowPausedPlayer = ({ connection, providerError = false } = {}) =>
  !providerError && !isDataSaverEnabled(connection)

// 止めて置いたプレーヤー（再生を試していない）
const isIdlePausedPlayer = ({ phase, attemptMode } = {}) =>
  phase === PLAYER_PHASES.MANUAL_PLAY_REQUIRED && !attemptMode

// 配信元のエラーの扱い。止めて置いたプレーヤーは、配信元の iframe ができていれば外さない（配信元の
// プレーヤーが知らせる）。Google の描画ではここでエラーになり、外すと動画が見つからない（仕様 0023）
const providerErrorAction = ({
  phase,
  attemptMode,
  hasProviderFrame = false,
} = {}) => {
  if (isIdlePausedPlayer({ phase, attemptMode }) && hasProviderFrame) {
    return { action: "keep" }
  }
  if (
    phase === PLAYER_PHASES.AUTOPLAY_ATTEMPTING ||
    phase === PLAYER_PHASES.MANUAL_PLAY_REQUIRED
  ) {
    return { action: "attempt-failed" }
  }
  return { action: "provider-error" }
}

const nextAttemptAfterFailure = ({ attemptMode }) => {
  if (attemptMode === ATTEMPT_MODES.MANUAL) {
    return { action: "provider-error" }
  }
  return { action: "manual-play" }
}

const parseYouTubeID = (value) => {
  if (typeof value !== "string" || value === "") return null
  if (YOUTUBE_ID_PATTERN.test(value)) return value

  var parsed
  try {
    parsed = new URL(value)
  } catch {
    return null
  }

  const hostname = parsed.hostname
  var candidate = null

  if (YOUTU_BE_HOST_PATTERN.test(hostname)) {
    candidate = parsed.pathname.split("/").filter(Boolean)[0]
  } else if (
    YOUTUBE_HOST_PATTERN.test(hostname) ||
    YOUTUBE_NOCOOKIE_HOST_PATTERN.test(hostname)
  ) {
    candidate = parsed.searchParams.get("v")
    if (!candidate) {
      const segments = parsed.pathname.split("/").filter(Boolean)
      if (
        ["embed", "shorts", "live", "v"].includes(segments[0]) &&
        segments.length > 1
      ) {
        candidate = segments[1]
      }
    }
  }

  return YOUTUBE_ID_PATTERN.test(candidate || "") ? candidate : null
}

const toPrivacyEnhancedYouTubeUrl = (value) => {
  const videoID = parseYouTubeID(value)
  if (!videoID) return null

  var source
  try {
    source = new URL(value)
  } catch {
    source = null
  }

  const result = new URL("https://www.youtube-nocookie.com/watch")
  result.searchParams.set("v", videoID)
  if (source) {
    ;["start", "end", "t", "list", "index"].forEach((name) => {
      const parameter = source.searchParams.get(name)
      if (parameter !== null) result.searchParams.set(name, parameter)
    })
  }
  return result.toString()
}

module.exports = {
  ATTEMPT_MODES,
  PLAYER_PHASES,
  isAutoplayViewportEligible,
  isDataSaverEnabled,
  isIdlePausedPlayer,
  nextAttemptAfterFailure,
  parseYouTubeID,
  providerErrorAction,
  shouldAutoplay,
  shouldShowPausedPlayer,
  toPrivacyEnhancedYouTubeUrl,
}
